import {
  clampMenuToViewport,
  ContextMenuItem,
  ContextMenuSurface,
} from '@src/components/common/contextMenuStyles';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import DeleteIcon from '@src/svgs/DeleteIcon';
import LinkIcon from '@src/svgs/LinkIcon';
import PencilIcon from '@src/svgs/PencilIcon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { useOutsideAlerter } from '../../transcript/useClickOutside';
import FolderIcon from './FolderIcon';

import type { Folder } from '@src/types.ts/story';
import type { FC, MouseEvent } from 'react';

type Props = {
  /** The current (deepest) folder in the breadcrumb trail. */
  folder: Folder;
  onCopyLink: (folder: Folder) => void;
  onRename: (folder: Folder) => void;
  onDelete: (folder: Folder) => void;
  onNewSubfolder: () => void;
};

const MENU_WIDTH = 200;
// Four rows. Feeds `clampMenuToViewport`, so it must track the row count.
const MENU_HEIGHT = 176;

// Actions menu on the current breadcrumb segment, so the folder you're in can
// be renamed/deleted (or get a new subfolder) without navigating to its parent.
const BreadcrumbFolderMenu: FC<Props> = ({
  folder,
  onCopyLink,
  onRename,
  onDelete,
  onNewSubfolder,
}) => {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setPos(null), []);
  useOutsideAlerter(portalRef, close);

  useEffect(() => {
    if (!pos) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [pos, close]);

  const handleTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (pos) {
      close();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setPos(
      clampMenuToViewport(rect.left, rect.bottom + 4, MENU_WIDTH, MENU_HEIGHT),
    );
  };

  const wrap = (cb: () => void) => (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    close();
    cb();
  };

  return (
    <>
      <Trigger
        type="button"
        onClick={handleTriggerClick}
        data-open={!!pos || undefined}
        aria-haspopup="menu"
        aria-expanded={!!pos}
        data-testid="breadcrumb-current-menu-trigger"
      >
        {folder.name}
        <Caret data-open={!!pos || undefined}>
          <ChevronDownIcon width="10" height="10" />
        </Caret>
      </Trigger>
      {pos &&
        createPortal(
          <ContextMenuSurface
            ref={portalRef}
            style={{ top: pos.y, left: pos.x, width: MENU_WIDTH }}
            data-testid="breadcrumb-current-menu"
          >
            <ContextMenuItem
              onClick={wrap(onNewSubfolder)}
              data-testid="breadcrumb-action-new-folder"
            >
              <FolderIcon /> New Folder
            </ContextMenuItem>
            {/* Sits under New Folder so Rename and Delete keep the position
                they already have at the bottom of this menu. */}
            <ContextMenuItem
              onClick={wrap(() => onCopyLink(folder))}
              data-testid="breadcrumb-action-copy-link"
            >
              <LinkIcon strokeColor="currentColor" /> Copy Link
            </ContextMenuItem>
            <ContextMenuItem
              onClick={wrap(() => onRename(folder))}
              data-testid="breadcrumb-action-rename"
            >
              <PencilIcon strokeColor="currentColor" /> Rename
            </ContextMenuItem>
            <ContextMenuItem
              $danger
              onClick={wrap(() => onDelete(folder))}
              data-testid="breadcrumb-action-delete"
            >
              <DeleteIcon /> Delete
            </ContextMenuItem>
          </ContextMenuSurface>,
          document.body,
        )}
    </>
  );
};

export default BreadcrumbFolderMenu;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 2px 4px;
  border-radius: var(--radius-button);
  font-family: inherit;
  font-size: inherit;
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  cursor: pointer;

  &:hover,
  &[data-open] {
    background: var(--surface-elevated);
  }
`;

const Caret = styled.span`
  display: inline-flex;
  color: var(--text-muted);
  transition: transform 120ms ease;

  &[data-open] {
    transform: rotate(180deg);
  }
`;
